import {
  AlertCircle,
  Mail,
  RefreshCw,
  Send,
  X,
} from "lucide-react";
import {
  useState,
} from "react";
import type { FormEvent } from "react";
import {
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import axios from "axios";

import {
  resendPlatformInvitation,
  revokePlatformInvitation,
  type PlatformInvitation,
  updatePlatformInvitationEmail,
} from "@/api/platformInvitations";

interface InvitationActionsProps {
  invitation: PlatformInvitation;
  onInvitationReplaced?: (
    invitation: PlatformInvitation,
  ) => void;
}

interface ApiErrorResponse {
  detail?: string;
  message?: string;
}

function getErrorMessage(error: unknown) {
  if (axios.isAxiosError<ApiErrorResponse>(error)) {
    const detail = error.response?.data?.detail;

    if (typeof detail === "string") {
      return detail;
    }

    const message = error.response?.data?.message;

    if (typeof message === "string") {
      return message;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "The invitation action could not be completed.";
}

export function InvitationActions({
  invitation,
  onInvitationReplaced,
}: InvitationActionsProps) {
  const queryClient = useQueryClient();

  const [isResendOpen, setIsResendOpen] =
    useState(false);

  const [isEmailOpen, setIsEmailOpen] =
    useState(false);

  const [expiresInDays, setExpiresInDays] =
    useState(7);

  const [email, setEmail] =
    useState(invitation.invited_email);

  const [firstName, setFirstName] =
    useState(
      invitation.invited_first_name ?? "",
    );

  const [lastName, setLastName] =
    useState(
      invitation.invited_last_name ?? "",
    );

  const [emailExpiresInDays, setEmailExpiresInDays] =
    useState(7);

  const [successMessage, setSuccessMessage] =
    useState<string | null>(null);

  async function refreshInvitationData() {
    await queryClient.invalidateQueries({
      queryKey: ["platform-invitations"],
    });
  }

  const resendMutation = useMutation({
    mutationFn: () =>
      resendPlatformInvitation(
        invitation.church_id,
        {
          expires_in_days: expiresInDays,
        },
      ),

    onSuccess: async (response) => {
      setSuccessMessage(
        response.message ||
          "Invitation resent successfully.",
      );

      setIsResendOpen(false);

      await refreshInvitationData();

      onInvitationReplaced?.(
        response.invitation,
      );
    },
  });

  const updateEmailMutation = useMutation({
    mutationFn: () =>
      updatePlatformInvitationEmail(
        invitation.church_id,
        {
          invited_email: email.trim(),
          invited_first_name:
            firstName.trim() || null,
          invited_last_name:
            lastName.trim() || null,
          expires_in_days:
            emailExpiresInDays,
        },
      ),

    onSuccess: async (response) => {
      setSuccessMessage(
        response.message ||
          "Administrator email updated.",
      );

      setIsEmailOpen(false);

      await refreshInvitationData();

      onInvitationReplaced?.(
        response.invitation,
      );
    },
  });

  const revokeMutation = useMutation({
    mutationFn: () => revokePlatformInvitation(invitation.church_id),
    onSuccess: async (response) => {
      setSuccessMessage(response.message);
      await refreshInvitationData();
      onInvitationReplaced?.(response.invitation);
    },
  });

  function handleResendSubmit(
    event: FormEvent<HTMLFormElement>,
  ) {
    event.preventDefault();

    setSuccessMessage(null);
    resendMutation.reset();
    resendMutation.mutate();
  }

  function handleEmailSubmit(
    event: FormEvent<HTMLFormElement>,
  ) {
    event.preventDefault();

    if (!email.trim()) {
      return;
    }

    setSuccessMessage(null);
    updateEmailMutation.reset();
    updateEmailMutation.mutate();
  }

  const resendError = resendMutation.isError
    ? getErrorMessage(resendMutation.error)
    : null;

  const emailError = updateEmailMutation.isError
    ? getErrorMessage(updateEmailMutation.error)
    : null;

  return (
    <>
      <section className="rounded-xl border border-slate-200 p-5">
        <div className="mb-4 flex items-center gap-2">
          <Send className="h-4 w-4 text-slate-500" />

          <h3 className="font-semibold text-slate-950">
            Invitation actions
          </h3>
        </div>

        {successMessage && (
          <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
            {successMessage}
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => {
              resendMutation.reset();
              setIsResendOpen(true);
            }}
            disabled={
              invitation.status.toUpperCase() ===
              "ACCEPTED"
            }
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RefreshCw className="h-4 w-4" />

            Resend invitation
          </button>

          <button
            type="button"
            onClick={() => {
              updateEmailMutation.reset();
              setIsEmailOpen(true);
            }}
            disabled={
              invitation.status.toUpperCase() ===
              "ACCEPTED"
            }
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Mail className="h-4 w-4" />

            Change administrator
          </button>
        </div>

        {invitation.status.toUpperCase() ===
          "ACCEPTED" && (
          <p className="mt-3 text-xs text-slate-500">
            Accepted invitations can no longer be
            resent or reassigned.
          </p>
        )}

        <div className="mt-5 border-t border-slate-200 pt-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
            Future actions
          </p>

          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => {
                if (window.confirm("Revoke this invitation? The current activation link will stop working.")) {
                  setSuccessMessage(null);
                  revokeMutation.mutate();
                }
              }}
              disabled={revokeMutation.isPending || ["ACCEPTED", "REVOKED"].includes(invitation.status.toUpperCase())}
              className="rounded-lg border border-rose-200 bg-white px-4 py-2 text-sm font-medium text-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {revokeMutation.isPending ? "Revoking..." : "Revoke invitation"}
            </button>

            <button
              type="button"
              disabled
              className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-medium text-slate-400"
            >
              Extend expiration
            </button>
          </div>
        </div>
        {revokeMutation.isError && (
          <p className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{getErrorMessage(revokeMutation.error)}</p>
        )}
      </section>

      {isResendOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4"
          role="presentation"
          onMouseDown={(event) => {
            if (
              event.target === event.currentTarget &&
              !resendMutation.isPending
            ) {
              setIsResendOpen(false);
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="resend-invitation-title"
            className="w-full max-w-md rounded-xl bg-white shadow-xl"
          >
            <div className="flex items-start justify-between border-b border-slate-200 px-6 py-5">
              <div>
                <h2
                  id="resend-invitation-title"
                  className="text-lg font-semibold text-slate-950"
                >
                  Resend invitation
                </h2>

                <p className="mt-1 text-sm text-slate-500">
                  Send a new invitation to{" "}
                  {invitation.invited_email}.
                </p>
              </div>

              <button
                type="button"
                onClick={() =>
                  setIsResendOpen(false)
                }
                disabled={resendMutation.isPending}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleResendSubmit}>
              <div className="space-y-4 px-6 py-5">
                <div>
                  <label
                    htmlFor="resend-expires-days"
                    className="text-sm font-medium text-slate-700"
                  >
                    Invitation expires in
                  </label>

                  <select
                    id="resend-expires-days"
                    value={expiresInDays}
                    onChange={(event) =>
                      setExpiresInDays(
                        Number(event.target.value),
                      )
                    }
                    className="mt-2 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                  >
                    <option value={1}>1 day</option>
                    <option value={3}>3 days</option>
                    <option value={7}>7 days</option>
                    <option value={14}>
                      14 days
                    </option>
                    <option value={30}>
                      30 days
                    </option>
                  </select>
                </div>

                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  A new invitation will replace the
                  currently active invitation.
                </div>

                {resendError && (
                  <div className="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />

                    <span>{resendError}</span>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-4">
                <button
                  type="button"
                  onClick={() =>
                    setIsResendOpen(false)
                  }
                  disabled={resendMutation.isPending}
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={resendMutation.isPending}
                  className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {resendMutation.isPending && (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  )}

                  {resendMutation.isPending
                    ? "Sending..."
                    : "Send invitation"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isEmailOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4"
          role="presentation"
          onMouseDown={(event) => {
            if (
              event.target === event.currentTarget &&
              !updateEmailMutation.isPending
            ) {
              setIsEmailOpen(false);
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="change-administrator-title"
            className="w-full max-w-lg rounded-xl bg-white shadow-xl"
          >
            <div className="flex items-start justify-between border-b border-slate-200 px-6 py-5">
              <div>
                <h2
                  id="change-administrator-title"
                  className="text-lg font-semibold text-slate-950"
                >
                  Change administrator
                </h2>

                <p className="mt-1 text-sm text-slate-500">
                  Update the person receiving this
                  church invitation.
                </p>
              </div>

              <button
                type="button"
                onClick={() =>
                  setIsEmailOpen(false)
                }
                disabled={
                  updateEmailMutation.isPending
                }
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleEmailSubmit}>
              <div className="space-y-4 px-6 py-5">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="administrator-first-name"
                      className="text-sm font-medium text-slate-700"
                    >
                      First name
                    </label>

                    <input
                      id="administrator-first-name"
                      type="text"
                      value={firstName}
                      onChange={(event) =>
                        setFirstName(
                          event.target.value,
                        )
                      }
                      maxLength={100}
                      className="mt-2 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="administrator-last-name"
                      className="text-sm font-medium text-slate-700"
                    >
                      Last name
                    </label>

                    <input
                      id="administrator-last-name"
                      type="text"
                      value={lastName}
                      onChange={(event) =>
                        setLastName(
                          event.target.value,
                        )
                      }
                      maxLength={100}
                      className="mt-2 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="administrator-email"
                    className="text-sm font-medium text-slate-700"
                  >
                    Email address
                  </label>

                  <input
                    id="administrator-email"
                    type="email"
                    required
                    value={email}
                    onChange={(event) =>
                      setEmail(event.target.value)
                    }
                    className="mt-2 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                  />
                </div>

                <div>
                  <label
                    htmlFor="email-expires-days"
                    className="text-sm font-medium text-slate-700"
                  >
                    New invitation expires in
                  </label>

                  <select
                    id="email-expires-days"
                    value={emailExpiresInDays}
                    onChange={(event) =>
                      setEmailExpiresInDays(
                        Number(event.target.value),
                      )
                    }
                    className="mt-2 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                  >
                    <option value={1}>1 day</option>
                    <option value={3}>3 days</option>
                    <option value={7}>7 days</option>
                    <option value={14}>
                      14 days
                    </option>
                    <option value={30}>
                      30 days
                    </option>
                  </select>
                </div>

                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  Changing the administrator creates
                  a new invitation and invalidates the
                  previous invitation link.
                </div>

                {emailError && (
                  <div className="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />

                    <span>{emailError}</span>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-4">
                <button
                  type="button"
                  onClick={() =>
                    setIsEmailOpen(false)
                  }
                  disabled={
                    updateEmailMutation.isPending
                  }
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={
                    updateEmailMutation.isPending ||
                    !email.trim()
                  }
                  className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {updateEmailMutation.isPending && (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  )}

                  {updateEmailMutation.isPending
                    ? "Saving..."
                    : "Save and send"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
